import { isConsolidationDevMocksEnabled } from "@/lib/consolidationDevFixtures";
import { DataSource, DATASOURCE_CONFIG } from "@/config/datasources";

/** Unleash flag gating consolidation rendering UI in production rollout. */
export const CONSOLIDATION_RENDERING_FEATURE_FLAG = "Consolidation-Rendering";

/**
 * Retsinformation datasource flag. Consolidation is a sub-feature of
 * Retsinformation and can never be enabled without it. Sourced from the
 * datasource config to avoid drift with the canonical flag name.
 */
export const RETSINFORMATION_FEATURE_FLAG =
  DATASOURCE_CONFIG[DataSource.Retsinformation].featureFlag ??
  "Kilde-Retsinformation";

const isConsolidationRenderingForcedLocal = (): boolean =>
  import.meta.env.DEV &&
  (import.meta.env.VITE_CONSOLIDATION_RENDERING === "true" ||
    import.meta.env.VITE_CONSOLIDATION_RENDERING === "1");

/**
 * Local dev bypass of the Unleash flags:
 * - `VITE_CONSOLIDATION_DEV_MOCKS=true` — fake metadata + UI (no Unleash)
 * - `VITE_CONSOLIDATION_RENDERING=true` — real silver metadata + UI (no Unleash)
 */
export const isConsolidationRenderingOverrideActive = (): boolean =>
  isConsolidationDevMocksEnabled() || isConsolidationRenderingForcedLocal();

/**
 * Whether consolidation metadata-driven UI (auto-open, outbound panel) is
 * active, given the per-user Unleash flag values. Explicit `?change_law=` /
 * `?as_of=` URL params bypass this and always work.
 *
 * The flag values must come from the Unleash frontend proxy
 * (useConsolidationRenderingEnabled), NOT from `GET /api/featureflag`: that
 * endpoint requires the admin-only `read:feature-flag` permission, so gating
 * on it silently hid all consolidation UI from non-admin users (LAR-374).
 */
export const isConsolidationRenderingEnabled = (flags: {
  hasConsolidation: boolean;
  hasRetsinformation: boolean;
}): boolean => {
  if (isConsolidationRenderingOverrideActive()) return true;

  return flags.hasConsolidation && flags.hasRetsinformation;
};
